import { Container } from "aurelia-dependency-injection";
import { defineComponent } from "vue";
import { mapState } from "pinia";

import { ToastrService } from "../../../../../../services/toastr.service";
import { Hospitalization } from "../../../../../../models/pal-health-history/hospitalization";
import { IGetPatientsSlimInfoResult } from "../../../../../../resources-vue/vue-interfaces/i-patient";
import { PatientService } from "../../../../../../services/patient.service";
import { AgencyRoles, ApplicationEnum } from "../../../../../../enums/enums";
import { IPageCallbackResult } from "../../../../../../resources-vue/vue-interfaces/i-page-container";
import { PAGE_FAIL_RESPONSE } from "../../../../../../resources-vue/vue-elements/Pagination/Pagination";
import { useConvertToPendingPal } from "../../../store/useConvertToPendingPal";
import { IHospitalization } from "../../../../../../resources-vue/vue-interfaces/i-health";

import Pagination from "../../../../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import HospitalizationsRow from "../HospitalizationsRow/HospitalizationsRow.vue";
import ErrorMessageHandler from "../../../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import HospitalAdmissionForm from "../HospitalAdmissionForm/HospitalAdmissionForm.vue";

export default defineComponent({
    components: {
        HospitalAdmissionForm,
        Pagination,
        ErrorMessageHandler,
        HospitalizationsRow
    },

    props: {
        patientId: {
            type: String,
            default: ""
        },
        providerId: {
            type: String,
            default: ""
        },
        accountId: {
            type: String
        }
    },

    data() {
        return {
            _patientsService: null as PatientService,
            _toastrService: null as ToastrService,
            isLoading: false as boolean,
            isSaving: false as boolean,
            isError: false as boolean,
            isAddRequested: false as boolean,
            newHospitalization: new Hospitalization() as Hospitalization,
            page: null,
            paginationInstance: null as typeof Pagination,
            columns: 6 as number,
            roles: Array.from(AgencyRoles.enumWithDescriptions.values()),
            patientInfo: null as IGetPatientsSlimInfoResult,
            loadingCount: 6,
            hasData: false as boolean
        };
    },

    created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._patientsService = Container.instance.get(PatientService);
        this.paginationInstance = this.$refs.paginationInstance;
    },

    async mounted() {
        this.paginationInstance = this.$refs.paginationInstance;
        const query = {
            application: ApplicationEnum.AxxessPalliative,
            agencyId: this.accountId
        };
        const response = await this._patientsService.getPatientSlim(this.patientId, query);
        this.patientInfo = response.data;
        this.reset();
    },

    computed: {
        ...mapState(useConvertToPendingPal, ["application"])
    },

    methods: {
        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            try {
                this.isLoading = true;
                let query = {
                    page: pageNumber,
                    pageLength: pageSize,
                    application: ApplicationEnum.AxxessPalliative,
                    accountId: this.accountId
                };
                let data = await this._patientsService.getHospitalizationsList(this.patientId, query);
                this.page = data.data;
                this.isLoading = false;
                return {
                    totalPageCount: data.data.PageCount,
                    success: data.data.ItemCount > 0,
                    totalCount: data.data.ItemCount
                };
            } catch (e) {
                console.error(e);
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.hasData = this.page?.ItemCount > 0;
            }
        },

        openAddHospitalization() {
            this.newHospitalization = new Hospitalization();
            this.isAddRequested = true;
        },

        async saveHospitalization(isChangeStatus: boolean = false) {
            try {
                this.isSaving = true;
                if (isChangeStatus) {
                    const payload = {
                        AccountId: this.accountId,
                        Application: this.application,
                        ...this.newHospitalization
                    };
                    await this._patientsService.updatePatientStatusToHospitalized(this.patientId, payload);
                    const query = {
                        application: ApplicationEnum.AxxessPalliative,
                        agencyId: this.accountId
                    };
                    const response = await this._patientsService.getPatientSlim(this.patientId, query);
                    this.patientInfo = response.data;
                } else {
                    const payload = {
                        Application: ApplicationEnum.AxxessPalliative,
                        AccountId: this.accountId,
                        PalliativePatientHospitalizationHistories: [this.newHospitalization]
                    };
                    await this._patientsService.createHospitalization(this.patientId, payload);
                }
                await this.reset();
                this._toastrService.success({
                    title: "Hospitalization Created",
                    message: "Hospitalization has been successfully created."
                });
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: "Error - Hospitalizations",
                    message: "There was an error creating Hospitalization. Please try again."
                });
            } finally {
                this.isSaving = false;
                this.cancelAddHospitalization();
            }
        },

        cancelAddHospitalization() {
            this.newHospitalization = new Hospitalization();
            this.isAddRequested = false;
        },

        async reset() {
            await this.paginationInstance.reset();
        },

        handleCancelEditHospitalization(index: number, previousData: IHospitalization) {
            this.page.Items[index] = previousData;
        }
    }
});
